import { AvatarMenuView } from "@bezoned/shared/components/avatar-menu/avatar-menu-view.jsx";
import { CalloutBlock } from "@bezoned/shared/components/callout-block.jsx";
import { FloatingWindowCard } from "@bezoned/shared/components/floating-window/floating-window-card.jsx";
import { FluentProvider, teamsLightTheme } from "@fluentui/react-components";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";

import type { AvatarMenuContext } from "../../hooks/use-avatar-menu.js";
import { useTeamsNavigation } from "../../hooks/use-teams-navigation.js";
import { useUserPhoto } from "../../hooks/use-user-photo.js";
import {
  getAvatarMenuInfo,
  sendChatMessage,
} from "../../services/api.service.js";
import { useLayoutStore } from "../../store/layout.store.js";
import { recordErrorInSentry } from "../../utils/init-sentry.js";
import { useAuth } from "../auth/use-auth.js";
import { useUserPresence } from "../presence/use-user-presence.js";
import {
  FloatingWindowRenderer,
  getFloatingWindowPositionFromAnchor,
  getFloatingWindowPositionNearAnchor,
} from "./floating-window-renderer.js";

interface AvatarMenuFrontendContainerProps {
  userId: string;
}

const AVATAR_MENU_POSITION_WIDTH_PX = 402;
const AVATAR_MENU_CARD_MAX_HEIGHT_PX = 450;
const AVATAR_MENU_GAP_PX = 12;
const RIGHT_MOUSE_BUTTON = 2;

export const AvatarMenuFrontendContainer = ({
  userId,
}: AvatarMenuFrontendContainerProps) => {
  const { user } = useAuth();
  const closeAvatarMenu = useLayoutStore((state) => state.closeAvatarMenu);
  const { avatar } = useUserPhoto({ msUserId: userId });
  const presence = useUserPresence({ msUserId: userId });
  const { navigateToChat, navigateToCall } = useTeamsNavigation({
    onCompleted: () => null,
  });
  const { data, isLoading, error } = useQuery({
    enabled: Boolean(userId),
    queryFn: () => getAvatarMenuInfo({ msUserId: userId }),
    queryKey: ["getAvatarMenuInfo", userId],
    staleTime: 30_000,
  });
  const selectedUserTeamsIdentifier = data?.info?.teamsUserIdentifier ?? null;
  const participantIdentifiers = useMemo(
    () =>
      [...new Set([selectedUserTeamsIdentifier, user?.email])].filter(
        (value): value is string =>
          value !== null && value !== undefined && value.length > 0
      ),
    [selectedUserTeamsIdentifier, user?.email]
  );

  if (isLoading) {
    return (
      <div className="flex h-full min-h-0 w-full flex-1 pr-1">
        <p>Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full min-h-0 w-full flex-1 pr-1">
        <CalloutBlock variant="error">
          <p>
            We are facing some difficulties while loading the user information.
            Please try again or report this issue to the support team.
          </p>
          <p>Thank you for your patience.</p>
        </CalloutBlock>
      </div>
    );
  }

  const handleStartChat = async () => {
    try {
      await navigateToChat(user?.tenantId, participantIdentifiers);
    } catch (caughtError) {
      recordErrorInSentry(caughtError);
    }
  };

  const handleStartCall = async () => {
    try {
      await navigateToCall(participantIdentifiers);
    } catch (caughtError) {
      recordErrorInSentry(caughtError);
    }
  };

  const handleQuickMessage = async (message: string) => {
    await sendChatMessage({ message, msUserId: userId });
    await navigateToChat(user?.tenantId, participantIdentifiers);
  };

  return (
    <div className="flex h-full min-h-0 w-full flex-1 pr-1">
      <AvatarMenuView
        availability={presence?.availability ?? null}
        email={data?.info?.email ?? null}
        events={data?.events ?? []}
        isOutOfOffice={presence?.outOfOffice?.isOutOfOffice ?? false}
        name={data?.info?.displayName ?? user?.name ?? "User"}
        onClose={closeAvatarMenu}
        onQuickMessageError={recordErrorInSentry}
        onSendQuickMessage={
          selectedUserTeamsIdentifier ? handleQuickMessage : undefined
        }
        onStartCall={selectedUserTeamsIdentifier ? handleStartCall : undefined}
        onStartChat={selectedUserTeamsIdentifier ? handleStartChat : undefined}
        phone={data?.info?.phone ?? null}
        avatar={avatar}
        statusMessage={presence?.statusMessage ?? null}
        timeZone={data?.timeZone ?? null}
      />
    </div>
  );
};

export const AvatarMenuWindow = () => {
  const { avatarMenu, closeAvatarMenu } = useLayoutStore();

  useEffect(() => {
    if (!avatarMenu) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (event.button === RIGHT_MOUSE_BUTTON) {
        return;
      }

      if (!(event.target instanceof Element)) {
        return;
      }

      if (event.target.closest("[data-avatar-menu]")) {
        return;
      }

      event.stopImmediatePropagation();
      closeAvatarMenu();
    };

    window.addEventListener("click", handleClickOutside);

    return () => {
      window.removeEventListener("click", handleClickOutside);
    };
  }, [avatarMenu, closeAvatarMenu]);

  if (!avatarMenu) {
    return null;
  }

  const avatarContext = avatarMenu.context as AvatarMenuContext;
  if (typeof window === "undefined") {
    return null;
  }

  const positionStyle =
    avatarContext.placement === "name-cell"
      ? getFloatingWindowPositionFromAnchor({
          anchor: avatarContext.position,
          horizontalOffsetPx: AVATAR_MENU_GAP_PX,
          panelHeightPx: AVATAR_MENU_CARD_MAX_HEIGHT_PX,
          panelWidthPx: AVATAR_MENU_POSITION_WIDTH_PX,
          verticalAlign: "top",
        })
      : getFloatingWindowPositionNearAnchor({
          anchor: avatarContext.position,
          panelHeightPx: AVATAR_MENU_CARD_MAX_HEIGHT_PX,
          panelWidthPx: AVATAR_MENU_POSITION_WIDTH_PX,
        });

  return createPortal(
    <FluentProvider theme={teamsLightTheme}>
      <FloatingWindowRenderer
        blocksHover={true}
        containerClassName="glass fixed z-[1010] flex animate-slide-in rounded-xl p-2"
        contentClassName="relative"
        panelClassName="box-border"
        panelRole="dialog"
        positionStyle={positionStyle}
      >
        <div data-avatar-menu data-avatar-menu-card>
          <FloatingWindowCard
            className="h-[450px] max-h-[450px] min-h-[350px] w-[400px]"
            padding="wide"
          >
            <AvatarMenuFrontendContainer userId={avatarContext.userId} />
          </FloatingWindowCard>
        </div>
      </FloatingWindowRenderer>
    </FluentProvider>,
    document.body
  );
};
